import React, { useEffect } from 'react'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import { Controller, useFormContext } from 'react-hook-form'
import { registrationCloseDates } from 'components/group-events/create-group-event-form-wizard/steps/external-registration-url'
import { buildDateTime } from 'lib/CustomLuxonUtils'
import { useStyles } from '../styles'

const HOURS_BEFORE_CLOSE_SELECT_LABEL_ID = 'hoursBeforeRegistrationCloses'
export const NO_REGISTRATION_CLOSE = 'none'

const noneRegistrationCloseOption = {
  hoursBefore: NO_REGISTRATION_CLOSE,
  label: 'None',
}

const RegistrationClose = ({
  eventStartDateTime,
  isExternalRegistration,
  timezone,
}) => {
  const classes = useStyles()
  const { control, watch, setValue, errors } = useFormContext()

  const registrationCloseDate = watch('registrationCloseDate')
  const registrationCloseTime = watch('registrationCloseTime')
  const hoursBeforeRegistrationCloses = watch('hoursBeforeRegistrationCloses')
  const registrationCloseOptions = isExternalRegistration
    ? [noneRegistrationCloseOption, ...registrationCloseDates]
    : registrationCloseDates

  const selectedHoursBeforeRegistrationCloses = (() => {
    if (!registrationCloseDate || !registrationCloseTime) {
      return isExternalRegistration
        ? NO_REGISTRATION_CLOSE
        : registrationCloseDates[0].hoursBefore
    }

    if (!eventStartDateTime || !timezone) {
      return registrationCloseDates[0].hoursBefore
    }

    // Keep the same "local time" model as `makeDefaultValues`:
    // registrationCloseDate/Time are Luxon DateTime values in the local zone.
    const startLocalDateTime = DateTime.fromISO(eventStartDateTime)
      .setZone(timezone)
      .setZone('local', { keepLocalTime: true })

    const closeLocalDateTime = buildDateTime(
      registrationCloseDate,
      registrationCloseTime,
    )

    const diffHours =
      (startLocalDateTime.toMillis() - closeLocalDateTime.toMillis()) /
      (60 * 60 * 1000)

    // Match the existing create-flow dropdown behavior: show the closest option.
    return (
      registrationCloseDates
        .map(option => ({
          hoursBefore: option.hoursBefore,
          distance: Math.abs(option.hoursBefore - diffHours),
        }))
        .sort((a, b) => a.distance - b.distance)[0]?.hoursBefore ??
      registrationCloseDates[0].hoursBefore
    )
  })()

  const hasError =
    !!errors?.registrationCloseDate || !!errors?.registrationCloseTime

  useEffect(() => {
    if (
      !eventStartDateTime ||
      !timezone ||
      (registrationCloseDate && !registrationCloseTime) ||
      (!registrationCloseDate && registrationCloseTime)
    ) {
      return
    }

    if (hoursBeforeRegistrationCloses !== undefined) return

    setValue(
      'hoursBeforeRegistrationCloses',
      selectedHoursBeforeRegistrationCloses,
    )
  }, [
    hoursBeforeRegistrationCloses,
    selectedHoursBeforeRegistrationCloses,
    eventStartDateTime,
    timezone,
    registrationCloseDate,
    registrationCloseTime,
    setValue,
  ])

  useEffect(() => {
    if (!eventStartDateTime || !timezone) return
    if (hoursBeforeRegistrationCloses === undefined) return

    if (
      isExternalRegistration &&
      hoursBeforeRegistrationCloses === NO_REGISTRATION_CLOSE
    ) {
      setValue('registrationCloseDate', null, {
        shouldValidate: true,
      })
      setValue('registrationCloseTime', null, {
        shouldValidate: true,
      })
      return
    }

    const hoursBefore = Number(hoursBeforeRegistrationCloses)
    if (Number.isNaN(hoursBefore)) return

    const startLocalDateTime = DateTime.fromISO(eventStartDateTime)
      .setZone(timezone)
      .setZone('local', { keepLocalTime: true })

    const computedCloseLocalDateTime = startLocalDateTime.minus({
      hours: hoursBefore,
    })

    // Keep the existing submit path intact: it still builds `registrationCloseDateTime`.
    setValue('registrationCloseDate', computedCloseLocalDateTime, {
      shouldValidate: true,
    })
    setValue('registrationCloseTime', computedCloseLocalDateTime, {
      shouldValidate: true,
    })
  }, [
    hoursBeforeRegistrationCloses,
    eventStartDateTime,
    isExternalRegistration,
    timezone,
    setValue,
  ])

  if (!eventStartDateTime || !timezone) {
    return null
  }

  return (
    <Box className={classes.fieldGroup} width="100%">
      <FormControl
        error={hasError}
        variant="outlined"
        fullWidth
        className={classes.fieldInput}
      >
        <InputLabel id={HOURS_BEFORE_CLOSE_SELECT_LABEL_ID}>
          Registration closes *
        </InputLabel>
        <Controller
          name="hoursBeforeRegistrationCloses"
          control={control}
          defaultValue={selectedHoursBeforeRegistrationCloses}
          as={
            <Select
              labelId={HOURS_BEFORE_CLOSE_SELECT_LABEL_ID}
              label="Registration closes *"
              data-cy="hoursBeforeRegistrationCloses"
            >
              {registrationCloseOptions.map(({ hoursBefore, label }) => (
                <MenuItem key={label} value={hoursBefore}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          }
        />
        {hasError ? (
          <Typography variant="caption" color="error">
            Please select an option
          </Typography>
        ) : null}
      </FormControl>
    </Box>
  )
}

export default RegistrationClose
